/* eslint-disable react-hooks/exhaustive-deps */
import DirectorNotesPanel from "components/DirectorNotesPanel";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchDirectorFeedbacks } from "services/directorFeedbackService";
import { DirectorFeedback } from "types";

type Props = {
  model: string;
  objectId?: string | number;
  role?: string;
};

// Floating launcher tab + slide-in drawer for the Director Notes panel, with an
// animated badge when there is new or action-required feedback.
const DirectorNotesButton: FC<Props> = ({ model, objectId, role }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [actionCount, setActionCount] = useState(0);
  const [hasNew, setHasNew] = useState(false);

  const seenKey = `dnotes_seen_${model}_${objectId}`;

  const loadCount = () => {
    if (!objectId) return;
    fetchDirectorFeedbacks({
      model,
      object_id: objectId,
      include_replies: true,
    })
      .then((data) => {
        const list = (Array.isArray(data) ? data : []).filter(
          (i: DirectorFeedback) => !i.parent,
        );
        setCount(list.length);
        setActionCount(
          list.filter(
            (i) =>
              (i.is_directive || i.feedback_type === "directive") &&
              i.status !== "resolved" &&
              i.status !== "acknowledged",
          ).length,
        );
        const latest = list.reduce(
          (m, i) => Math.max(m, new Date(i.created_at || 0).getTime()),
          0,
        );
        let seen = 0;
        try {
          seen = Number(localStorage.getItem(seenKey) || 0);
        } catch {}
        setHasNew(list.length > 0 && latest > seen);
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadCount();
  }, [model, objectId]);

  const openDrawer = () => {
    setOpen(true);
    try {
      localStorage.setItem(seenKey, String(Date.now()));
    } catch {}
    setHasNew(false);
  };

  const closeDrawer = () => {
    setOpen(false);
    loadCount();
  };

  const pulse = hasNew || actionCount > 0;

  if (!objectId) return null;

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={openDrawer}
        title={t("director_notes", { defaultValue: "Director Notes" })}
        className="fixed z-40 bottom-6 right-6 inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-300/40 hover:shadow-xl hover:scale-105 transition-all"
      >
        {/* Pulsing ring when new / action-required */}
        {pulse && (
          <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-30" />
        )}
        <span className="relative text-lg leading-none">🗒️</span>
        <span className="relative text-sm font-semibold hidden sm:inline">
          {t("director_notes", { defaultValue: "Director Notes" })}
        </span>
        {count > 0 && (
          <span
            className={`relative inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[11px] font-bold ${
              pulse ? "bg-white text-red-700" : "bg-red-800/60 text-white"
            }`}
          >
            {count}
          </span>
        )}
        {actionCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
          </span>
        )}
      </button>

      {/* Drawer */}
      <div
        className={`fixed inset-0 z-50 ${
          open ? "" : "pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        {/* Backdrop */}
        <div
          onClick={closeDrawer}
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            open ? "opacity-100" : "opacity-0"
          }`}
        />
        {/* Panel */}
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-md bg-gray-50 shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-white shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-lg">🗒️</span>
              <h3 className="text-base font-semibold text-gray-900">
                {t("director_notes", { defaultValue: "Director Notes" })}
              </h3>
              {actionCount > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                  {actionCount} {t("requires_action", { defaultValue: "Requires Action" })}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={closeDrawer}
              aria-label="Close"
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {open && (
              <DirectorNotesPanel
                embedded
                model={model}
                objectId={objectId}
                role={role}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DirectorNotesButton;
